import type { ComponentType, CSSProperties } from 'react';

import { useLibraryIcon } from 'hooks/useLibraryIcon';
import { ICON_SET_SCALE } from 'helpers/iconSet';
import type { IconComponent, IconSet, IconSlug, IIconProps } from 'types/icons';

export interface ISetIconProps extends IIconProps {
  slug: IconSlug;
  fallback: ComponentType<any>;
  rotate?: number;
  // stroke for a set glyph; `style.strokeWidth` is tuned for the fallback artwork and is dropped
  setStrokeWidth?: number;
}

interface ILibraryIconProps extends IIconProps {
  Svg: IconComponent;
  set: IconSet;
  rotate?: number;
  setStrokeWidth?: number;
}

const LibraryIcon = ({
  Svg,
  set,
  rotate,
  setStrokeWidth,
  size,
  color,
  className,
  style,
  ...restProps
}: ILibraryIconProps) => {
  // `fill` belongs to the legacy artwork, which colours through it. A library glyph is
  // currentColor-driven, so a literal fill turns a stroked set icon into a solid blob.
  const { width, height, fill, ...rest } = restProps;

  const box =
    (typeof size === 'number' && size) ||
    (typeof width === 'number' && width) ||
    (typeof height === 'number' && height) ||
    undefined;

  const scale = ICON_SET_SCALE[set];

  const iconStyle: CSSProperties = {
    fontSize: box ? `${box * scale}px` : (scale !== 1 && `${scale}em`) || undefined,
    // Inline `currentColor` would beat a wrapper class's `color:` here, then resolve against
    // the parent.
    color: color && color !== 'currentColor' ? color : undefined,
    transform: rotate ? `rotate(${rotate}deg)` : undefined,
    // Khushmeen's ink touches the viewBox edge; at 1x DPR the snapped svg box clipped the last
    // pixel column off (desktop rail). The ink never leaves the viewBox, so nothing else shows.
    overflow: set === 'khushmeen' ? 'visible' : undefined,
    ...style,
    strokeWidth: setStrokeWidth
  };

  // svgr spreads props after its own width="1em", so an undefined value deletes that attribute
  // and the icon fills its container.
  const dimensions: { width?: string | number; height?: string | number } = {};

  if (width !== undefined && typeof width !== 'number') dimensions.width = width;
  if (height !== undefined && typeof height !== 'number') dimensions.height = height;

  return (
    <Svg className={className} style={iconStyle} data-icon-set={set} {...dimensions} {...rest} />
  );
};

/** `data-icon-set` lets surface CSS exclude set icons — `&:not([data-icon-set])`. */
export const SetIcon = ({ slug, fallback: Fallback, setStrokeWidth, ...props }: ISetIconProps) => {
  const library = useLibraryIcon(slug);

  if (library) return <LibraryIcon {...library} {...props} setStrokeWidth={setStrokeWidth} />;

  return <Fallback {...props} />;
};

export const createSetIcon = (slug: IconSlug, fallback: ComponentType<any>) => {
  const Wrapped = (props: IIconProps) => <SetIcon slug={slug} fallback={fallback} {...props} />;

  Wrapped.displayName = `SetIcon(${slug})`;

  return Wrapped;
};
